import { DeviceLogs } from "@model/DeviceLogs";

export interface DeviceLogEntry {
  _id?: string;
  data?: string;
  pid?: string;
  createdAt?: string;
}

export type DeviceLogSeverity = "error" | "success" | "progress" | "info" | "unknown";

// Covers the whole enum: anything unmapped used to fall through to green.
const SEVERITY_BY_CODE: Record<string, DeviceLogSeverity> = {
  [DeviceLogs.OSDownloadFailed]: "error",
  [DeviceLogs.OSUpdateFailed]: "error",
  [DeviceLogs.AppDownloadFailed]: "error",
  [DeviceLogs.AppUpdateFailed]: "error",
  [DeviceLogs.BrightnessChangeError]: "error",
  [DeviceLogs.UnkownManagementConfigType]: "error",
  [DeviceLogs.ManagementConfigApplyFailed]: "error",
  [DeviceLogs.WifiRequestFailed]: "error",
  [DeviceLogs.WifiRequestIgnored]: "error",

  [DeviceLogs.OSDownloadSuccess]: "success",
  [DeviceLogs.OSUpdateSuccess]: "success",
  [DeviceLogs.AppDownloadSuccess]: "success",
  [DeviceLogs.AppUpdateSuccess]: "success",
  [DeviceLogs.BootCompleted]: "success",
  [DeviceLogs.ManagementConfigApplied]: "success",

  [DeviceLogs.DownloadTriggered]: "progress",
  [DeviceLogs.OSUpdateTriggered]: "progress",
  [DeviceLogs.AppUpdateTriggered]: "progress",
  [DeviceLogs.RebootTriggered]: "progress",
  [DeviceLogs.RebootStarted]: "progress",
  [DeviceLogs.ManagementConfigUpdateTriggered]: "progress",
  [DeviceLogs.ManagementConfigResetTriggered]: "progress",
  [DeviceLogs.AutoShutdown]: "progress",

  [DeviceLogs.BrightnessChanged]: "info",
  [DeviceLogs.VolumeChanged]: "info",
  [DeviceLogs.TimeoutsChanged]: "info",
  [DeviceLogs.SystemSettingsChanged]: "info",
  [DeviceLogs.HomeLauncherChanged]: "info",
  [DeviceLogs.WifiSettingsChanged]: "info",
  [DeviceLogs.IpdSet]: "info",
  [DeviceLogs.PermissionsChanged]: "info",
  [DeviceLogs.DownloadSkipped]: "info",
};

export const DEVICE_LOG_SEVERITY_COLOR: Record<DeviceLogSeverity, string> = {
  error: "red",
  success: "green",
  progress: "orange",
  info: "blue",
  unknown: "gray",
};

export const deviceLogCode = (entry: DeviceLogEntry): string =>
  (entry.data ?? "").trim().split(/\s+/)[0] ?? "";

export const deviceLogDetail = (entry: DeviceLogEntry): string =>
  (entry.data ?? "").trim().split(/\s+/).slice(1).join(" ");

export const deviceLogSeverity = (entry: DeviceLogEntry): DeviceLogSeverity => {
  const code = deviceLogCode(entry);
  const known = SEVERITY_BY_CODE[code];
  if (known) return known;
  // Fall back on the wording so an unmapped failure still reads as one.
  return /fail|error/i.test(code) ? "error" : "unknown";
};

export const DEVICE_LOG_CODES: string[] = Object.values(DeviceLogs);

export interface DeviceLogsPage {
  logs: DeviceLogEntry[];
  total: number | null;
}

// Older backends answer with a bare array, which carries no total.
export const parseDeviceLogsResponse = (response: unknown): DeviceLogsPage => {
  const body = (response as { data?: unknown })?.data;

  if (Array.isArray(body)) {
    return { logs: body as DeviceLogEntry[], total: null };
  }

  const paged = body as { data?: unknown; total?: unknown } | null | undefined;
  if (paged && Array.isArray(paged.data)) {
    return {
      logs: paged.data as DeviceLogEntry[],
      total: typeof paged.total === "number" ? paged.total : null,
    };
  }

  return { logs: [], total: null };
};
